import React, { useCallback, useState } from 'react';

import Button from '../../../common/Button/Button';
import CardView from '../../../common/CardView/CardView';
import ContactSupport from '../../../common/Labels/ContactSupportLink';
import EmailInput from '../../../common/TextInputs/EmailInput/EmailInput';
import Label from '../../../common/Labels/Label';
import LinkButton from '../../../common/Labels/LinkButton';
import ModalOverlay from '../../../common/ModalOverlay/ModalOverlay';
import RadioGroup from '../../../common/RadioGroup/RadioGroup';
import TextInputWithError from '../../../common/TextInputs/TextInputWithError/TextInputWithError';

import { isEmail } from '../../../common/TextInputs/EmailInput/email-utils';

const USER_STATUS_ITEMS = [
  {
    id: 'active',
    label: 'Active',
  },
  {
    id: 'inactive',
    label: 'Inactive',
  }
]

const ROLE_ITEMS = [
  {
    id: 'admin',
    label: 'Admin',
  },
  {
    id: 'user',
    label: 'User',
  }
]

export const EditUserControlSection = ({
  firstName,
  lastName,
  email,
  isAdmin = false,
  isUserActive = false,
  setUserActive = () => {},
  setUserAdmin = () => {},
  setFirstName = () => {},
  setLastName = () => {},
  setEmail = () => {},
  onPasswordReset = () => {},
  setEmailError = () => {},
}) => {
  const onPasswordGroupSelect = (selectedType) => {
    setUserActive(selectedType === 'active');
  }
  const onRoleSelect = (selectedRole) => {
    setUserAdmin(selectedRole === 'admin');
  }

  const onEntryValidation = entry => entry?.length;
  const emailError = !email?.length ? 'Invalid email' : null;

  return (
    <div className="flex flex-col justify-start items-start w-filter-modal h-80">
      <div className="grid grid-cols-4 gap-1 m-2 w-full">
        <div className="flex flex-col items-start justify-start col-span-1">
          <Label className="my-1" bold>Status</Label>
          <RadioGroup
            id="active-mode-selection"
            selectedItemId={isUserActive ? 'active' : 'inactive'}
            items={USER_STATUS_ITEMS}
            onSelect={onPasswordGroupSelect}
            renderAsRow
          />
        </div>
        <div className="flex flex-col col-span-2 items-start justify-start">
          <Label className="my-1" bold>Email</Label>
          <EmailInput
            value={email}
            onChange={setEmail}
            onEntryValidation={onEntryValidation}
            errorMessage={emailError}
            onValueError={setEmailError}
            disabled={!isUserActive}
            block
          />
        </div>
        <div className="flex flex-col items-center justify-start col-span-1">
          <Label className="my-1" bold>Reset Password</Label>
          <Button sx={{ px: 6 }} onClick={onPasswordReset} disabled={!isUserActive}>
            Reset
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 m-2 w-full">
        <div className="flex flex-col items-start justify-start">
          <Label bold>First Name</Label>
          <TextInputWithError
            value={firstName}
            onChange={setFirstName}
            onEntryValidation={onEntryValidation}
            disabled={!isUserActive}
            block
          />
        </div>
        <div className="flex flex-col items-start justify-start">
          <Label bold>Last Name</Label>
          <TextInputWithError
            value={lastName}
            onChange={setLastName}
            onEntryValidation={onEntryValidation}
            disabled={!isUserActive}
            block
          />
        </div>
      </div>
      <div className="flex flex-col items-start justify-start m-2">
        <Label bold>
          Type
        </Label>
        <RadioGroup
          id="role-selection"
          selectedItemId={isAdmin ? 'admin' : 'user'}
          items={ROLE_ITEMS}
          onSelect={onRoleSelect}
          disabled={!isUserActive}
          renderAsRow
        />
      </div>
    </div>
  )
}

export const EditUserPanel = ({
  userId,
  error = null,
  user = {},
  onConfirm = () => {},
  onCancel = () => {},
  onDelete = () => {},
  onPasswordReset = () => {},
}) => {
  const wasAdmin = user?.realmRoles?.includes('admin') || false;
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [isAdmin, setAdmin] = useState(wasAdmin);
  const [isActive, setActive] = useState(user?.enabled || false);

  const handleApplyEdit = useCallback(() => {
    onConfirm({
      id: userId,
      firstName,
      lastName,
      email,
      isAdmin,
      isActive,
    })
  }, [email, firstName, isActive, isAdmin, lastName, onConfirm, userId])
  
  const disableConfirmationButton = !email?.length || !isEmail(email);
  
  return (
    <CardView disableFullHeight>
      <div className="flex justify-between items-center m-1">
        <Label size="2xl" bold>Edit User</Label>
        <Button variant="close" onClick={onCancel}/>
      </div>
      <EditUserControlSection
        firstName={firstName}
        lastName={lastName}
        email={email}
        isAdmin={isAdmin}
        isUserActive={isActive}
        setEmail={setEmail}
        setFirstName={setFirstName}
        setLastName={setLastName}
        setUserActive={setActive}
        setUserAdmin={setAdmin}
        onPasswordReset={onPasswordReset}
      />
      {
        error && (
          <div className="p-1 self-center">
            <Label color="error" size="sm">
              Error while editing the user. Please try again or <ContactSupport>contact support</ContactSupport> if the issue persists.
            </Label>
          </div>
        )
      }
      <div className="flex justify-between m-1">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex">
          <LinkButton color="error" hoverColor="error" activeColor="error" onClick={onDelete} showUnderline>Delete User</LinkButton>
          <Button className="ml-2" disabled={disableConfirmationButton} variant="primary" onClick={handleApplyEdit}>
            Save
          </Button>
        </div>
      </div>
    </CardView>
  )
}

export const EditUserDialog = ({
  userId,
  user,
  error = null,
  onPasswordReset = () => {},
  onUserEdited = () => {},
  onUserDeleted = () => {},
  showDialog = false,
  onDialogToggle = () => {},
}) => {
  const handleConfirmation = useCallback((editedUser) => {
    onUserEdited(editedUser);
  }, [onUserEdited]);

  const handleDelete = useCallback(() => {
    onUserDeleted(userId);
  }, [onUserDeleted, userId]);

  const handlePasswordReset = useCallback(() => {
    onPasswordReset(userId);
  }, [onPasswordReset, userId])

  return (
    <ModalOverlay animationKey="admin-edit-user-modal" onModalToggle={onDialogToggle} active={showDialog} useScrollLock={false}>
      <EditUserPanel
        error={error}
        userId={userId}
        user={user}
        onConfirm={handleConfirmation}
        onCancel={() => onDialogToggle(false)}
        onDelete={handleDelete}
        onPasswordReset={handlePasswordReset}
      />
    </ModalOverlay>
  )
}

export default EditUserDialog;
